import React from 'react';
import { X } from 'lucide-react';

import { AppModal } from '../../../components/AppModal';
import { APP_INPUT_CLASS } from '../../../ui/controlStyles';
import { parseGermanNumberOrNull } from '../../../utils/parseGermanNumber';
import { formatEuro } from '../utils/offerDocumentUtils';
import {
  computeSurchargeLinePrice,
  computeSurchargeUnitPrice,
  type SurchargeTargetCandidate,
} from '../utils/positionSurcharge';
import type { OfferPositionSurchargeConfig, OfferPositionSurchargeMode } from '../../../types';

export type PositionSurchargePickerModalProps = {
  open: boolean;
  mode: 'insert' | 'edit';
  onClose: () => void;
  candidates: SurchargeTargetCandidate[];
  initialPercent: number;
  initialAmount?: number | null;
  initialSurchargeMode?: OfferPositionSurchargeMode;
  initialTargetRowIds: string[];
  onConfirm: (config: OfferPositionSurchargeConfig) => void | Promise<void>;
};

function formatDecimalInput(n: number): string {
  return n > 0 ? String(n).replace('.', ',') : '';
}

export const PositionSurchargePickerModal: React.FC<PositionSurchargePickerModalProps> = ({
  open,
  mode,
  onClose,
  candidates,
  initialPercent,
  initialAmount = null,
  initialSurchargeMode = 'percent',
  initialTargetRowIds,
  onConfirm,
}) => {
  const [surchargeKind, setSurchargeKind] = React.useState<OfferPositionSurchargeMode>(
    initialSurchargeMode === 'amount' ? 'amount' : 'percent',
  );
  const [percentText, setPercentText] = React.useState(() => formatDecimalInput(initialPercent));
  const [amountText, setAmountText] = React.useState(() =>
    formatDecimalInput(typeof initialAmount === 'number' ? initialAmount : 0),
  );
  const [draftTargetIds, setDraftTargetIds] = React.useState<string[]>(initialTargetRowIds);

  React.useEffect(() => {
    if (!open) return;
    setSurchargeKind(initialSurchargeMode === 'amount' ? 'amount' : 'percent');
    setPercentText(formatDecimalInput(initialPercent));
    setAmountText(formatDecimalInput(typeof initialAmount === 'number' ? initialAmount : 0));
    setDraftTargetIds(initialTargetRowIds);
  }, [open, initialPercent, initialAmount, initialSurchargeMode, initialTargetRowIds]);

  const draftPercent = parseGermanNumberOrNull(percentText) ?? 0;
  const draftAmount = parseGermanNumberOrNull(amountText) ?? 0;

  const candidateIds = React.useMemo(() => new Set(candidates.map((c) => c.id)), [candidates]);
  const draftTargetSet = React.useMemo(() => new Set(draftTargetIds), [draftTargetIds]);

  const baseSum = React.useMemo(() => {
    let sum = 0;
    for (const c of candidates) {
      if (!draftTargetSet.has(c.id)) continue;
      sum += c.lineTotal;
    }
    return Math.round(sum * 100) / 100;
  }, [candidates, draftTargetSet]);

  const draftConfig: OfferPositionSurchargeConfig =
    surchargeKind === 'amount'
      ? { mode: 'amount', percent: 0, amount: draftAmount, targetRowIds: draftTargetIds }
      : { mode: 'percent', percent: draftPercent, targetRowIds: draftTargetIds };
  const linePrice = computeSurchargeLinePrice(baseSum, draftConfig);
  const hasTargets = draftTargetIds.some((id) => candidateIds.has(id));
  const canConfirm =
    hasTargets && (surchargeKind === 'amount' ? draftAmount > 0 : draftPercent > 0);

  const [confirming, setConfirming] = React.useState(false);

  const toggleId = (id: string) => {
    setDraftTargetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const switchKind = (next: OfferPositionSurchargeMode) => {
    if (next === surchargeKind) return;
    if (next === 'amount') {
      const preview = computeSurchargeUnitPrice(baseSum, draftPercent);
      if (preview > 0) setAmountText(formatDecimalInput(preview));
    } else if (baseSum > 0 && draftAmount > 0) {
      const pct = Math.round((draftAmount / baseSum) * 10000) / 100;
      const capped = Math.min(100, pct);
      if (capped > 0) setPercentText(formatDecimalInput(capped));
    }
    setSurchargeKind(next);
  };

  const handleConfirm = async () => {
    const validIds = draftTargetIds.filter((id) => candidateIds.has(id));
    if (!validIds.length || confirming) return;
    const config: OfferPositionSurchargeConfig =
      surchargeKind === 'amount'
        ? { mode: 'amount', percent: 0, amount: draftAmount, targetRowIds: validIds }
        : { mode: 'percent', percent: draftPercent, targetRowIds: validIds };
    if (computeSurchargeLinePrice(baseSum, config) <= 0) return;
    setConfirming(true);
    try {
      await onConfirm(config);
      onClose();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      overlayClassName="z-[272]"
      panelClassName="md:max-w-lg"
      ariaLabelledBy="position-surcharge-picker-title"
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-app-border shrink-0">
          <h2 id="position-surcharge-picker-title" className="text-sm font-semibold text-app-text">
            {mode === 'insert' ? 'Zuschlag auf vorherige Positionen' : 'Zuschlag bearbeiten'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-1 rounded-md text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 text-[11px] text-app-text-muted border-b border-app-border shrink-0">
          Wählen Sie die Positionen oberhalb dieser Zeile und den Zuschlag als Prozent oder Betrag.
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-4 py-3 space-y-4">
          <div className="space-y-2">
            <p className="block text-xs font-medium text-app-text-muted">Zuschlag</p>
            <div
              role="group"
              aria-label="Zuschlagsart"
              className="flex h-9 w-full items-stretch rounded-md border border-app-border bg-app-surface-muted p-0.5"
            >
              <button
                type="button"
                onClick={() => switchKind('percent')}
                className={`flex-1 rounded-md text-sm font-medium transition-colors ${
                  surchargeKind === 'percent'
                    ? 'bg-app-surface text-app-text shadow-sm'
                    : 'text-app-text-muted hover:text-app-text'
                }`}
              >
                Nach Prozent
              </button>
              <button
                type="button"
                onClick={() => switchKind('amount')}
                className={`flex-1 rounded-md text-sm font-medium transition-colors ${
                  surchargeKind === 'amount'
                    ? 'bg-app-surface text-app-text shadow-sm'
                    : 'text-app-text-muted hover:text-app-text'
                }`}
              >
                Nach Betrag
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={surchargeKind === 'percent' ? percentText : amountText}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!/^[0-9]*([.,][0-9]*)?$/.test(raw)) return;
                  if (surchargeKind === 'percent') {
                    const num = parseGermanNumberOrNull(raw);
                    if (num == null || num <= 100) setPercentText(raw);
                    return;
                  }
                  setAmountText(raw);
                }}
                placeholder={surchargeKind === 'percent' ? 'z. B. 20,5' : 'z. B. 150,00'}
                aria-label={surchargeKind === 'percent' ? 'Zuschlag in Prozent' : 'Zuschlag in Euro'}
                className={`${APP_INPUT_CLASS} max-w-[10rem] text-right tabular-nums`}
              />
              <span className="text-sm text-app-text-muted">{surchargeKind === 'percent' ? '%' : '€'}</span>
            </div>
          </div>

          {candidates.length === 0 ? (
            <p className="text-sm text-app-text-subtle py-4 text-center">
              Keine vorherigen Positionen mit Preis verfügbar.
            </p>
          ) : (
            <table className="w-full border-collapse text-[11px] text-app-text">
              <thead>
                <tr className="bg-app-surface-muted border-b border-app-border">
                  <th className="w-8 px-2 py-1.5" />
                  <th className="text-left font-semibold px-2 py-1.5 w-12">Pos.</th>
                  <th className="text-left font-semibold px-2 py-1.5">Kurztext</th>
                  <th className="text-right font-semibold px-2 py-1.5">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.id} className="border-b border-app-border hover:bg-app-hover/80">
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={draftTargetSet.has(c.id)}
                        onChange={() => toggleId(c.id)}
                        aria-label={`Position ${c.numberLabel} auswählen`}
                      />
                    </td>
                    <td className="px-2 py-1.5 tabular-nums text-app-text-muted">{c.numberLabel}</td>
                    <td className="px-2 py-1.5">{c.item_name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatEuro(c.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="shrink-0 border-t border-app-border px-4 py-3 space-y-3">
          <p className="text-sm text-app-text tabular-nums">
            {surchargeKind === 'percent' ? (
              <>
                Summe {formatEuro(baseSum)} × {draftPercent.toLocaleString('de-DE')} % ={' '}
                <span className="font-semibold">{formatEuro(linePrice)}</span>
              </>
            ) : (
              <>
                Summe der Positionen {formatEuro(baseSum)}. Zuschlag ={' '}
                <span className="font-semibold">{formatEuro(linePrice)}</span>
              </>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center px-3.5 text-sm rounded-md border border-app-border text-app-text-muted hover:bg-app-hover"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm || confirming}
              className="inline-flex h-9 items-center px-3.5 text-sm rounded-md bg-pipedrive-blue text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
            >
              {confirming ? 'Wird gespeichert…' : mode === 'insert' ? 'Einfügen' : 'Übernehmen'}
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  );
};
